import { FrontendBookmarkItem } from '@reach/interfaces/bookmarks/bookmarks.interface';
import { NodeType } from '@reach/interfaces/nodes/node-type.enum';

import { BookmarkItem, BookmarkSelectState } from '../types';

export function transformBookmarks(givenBookmarks: FrontendBookmarkItem[]): BookmarkItem[] {
	const bookmarks: BookmarkItem[] = givenBookmarks
		.map((b) => pluginItemToWeb(b))
		.filter((b) => !!b);

	return cleanBookmarksTree(bookmarks);
}

export function computeSelectionState(item: BookmarkItem): void {
	if (!item.folder) {
		return;
	}

	for (const child of item.children) {
		computeSelectionState(child);
	}

	const selectedCount = item.children.reduce((acc, c) => {
		const curr = c.selected === BookmarkSelectState.UNSELECTED ? 0 : 1;
		return acc + curr;
	}, 0);

	const selectedState =
		selectedCount === 0
			? BookmarkSelectState.UNSELECTED
			: selectedCount === item.children.length
				? BookmarkSelectState.SELECTED
				: BookmarkSelectState.SOME_SELECTED;
	item.selected = selectedState;

	if (selectedState === BookmarkSelectState.UNSELECTED) {
		item.collapsed = true;
	}
}

function pluginItemToWeb(
	webBookmark: FrontendBookmarkItem,
	parent?: BookmarkItem
): BookmarkItem | null {
	const { id, name, href: url } = webBookmark;
	const folder = webBookmark.nodeType === NodeType.TAG;
	const selected = BookmarkSelectState.SELECTED;
	const collapsed = true;

	const item: BookmarkItem = {
		id,
		folder,
		name,
		url,
		selected,
		collapsed,
		children: [],
		parent,
	};

	const children = webBookmark.children.map((c) => pluginItemToWeb(c, item)).filter((c) => !!c);
	item.children = children;

	return item;
}

function sortEverything(bookmarks: BookmarkItem[]): void {
	bookmarks.forEach((item) => {
		sortEverything(item.children);
	});

	bookmarks.sort((itemA, itemZ) => {
		if (itemA.folder && !itemZ.folder) {
			return -1;
		} else if (itemZ.folder && !itemA.folder) {
			return +1;
		}

		return itemA.name.normalize().localeCompare(itemZ.name.normalize());
	});
}

function cleanBookmarksTree(bookmarks: BookmarkItem[]): BookmarkItem[] {
	const browserCleaned = cleanTreeFromBrowser(bookmarks);
	const withoutEmptyFolder = cleanTreeFromEmptyFolders(browserCleaned);
	const withoutEmptyWebsites = removeEmptyWebsites(withoutEmptyFolder);
	withoutEmptyWebsites.forEach((b) => computeSelectionState(b));
	sortEverything(withoutEmptyWebsites);
	return withoutEmptyWebsites;
}

function cleanTreeFromBrowser(tree: BookmarkItem[]): BookmarkItem[] {
	let newTree: BookmarkItem[] = [];
	for (const rootChild of newTree) {
		rootChild.parent = undefined;
	}

	const browser = getBrowser();
	if (browser === 'firefox') {
		newTree = cleanTreeFromFirefox(tree);
	} else if (browser === 'chrome') {
		newTree = cleanTreeFromChrome(tree);
	}

	return newTree;
}

function cleanTreeFromEmptyFolders(tree: BookmarkItem[]): BookmarkItem[] {
	const newTree: BookmarkItem[] = [];

	for (const child of tree) {
		child.children = cleanTreeFromEmptyFolders(child.children || []);

		const withoutChildren = (child.children || []).length === 0;
		if (!child.folder || !withoutChildren) {
			newTree.push(child);
		}
	}

	return newTree;
}

function removeEmptyWebsites(tree: BookmarkItem[]): BookmarkItem[] {
	const newTree: BookmarkItem[] = [];
	for (const item of tree) {
		const expandable = item.folder;
		const url = (item.url || '').trim();
		const hasUrl = url !== '' && url !== 'data:';

		if (expandable || hasUrl) {
			newTree.push(item);
		}

		if (expandable) {
			item.children = removeEmptyWebsites(item.children || []);
		}

		if (url === 'debug:deselect') {
			deselectItem(item);
		}
	}
	return newTree;
}

function cleanTreeFromFirefox(tree: BookmarkItem[]): BookmarkItem[] {
	const newTree: BookmarkItem[] = [];

	for (const item of tree) {
		let addItem = true;

		// Move items' children up
		switch (item.id) {
			case 'menu________':
			case 'toolbar_____':
			case 'unfiled_____':
			case 'mobile______':
				item.children = cleanTreeFromFirefox(item.children);
				addItem = false;
				item.children.forEach((c) => {
					c.parent = item.parent;
				});
				newTree.push(...item.children);
		}

		// Move items' children up
		if ((item.name || '').trim().toLowerCase() === 'bookmarks') {
			addItem = false;
			item.children.forEach((c) => {
				c.parent = item.parent;
			});
			newTree.push(...item.children);
		}

		// Deselect Firefox and OS folders recursivelly
		if (item.id === 'menu________') {
			for (const child of item.children || []) {
				switch (child.name) {
					case 'Mozilla Firefox':
					case 'Ubuntu and Free Software links':
					case 'Manjaro Linux':
						deselectItem(child, true);
				}
			}
		}

		// Deselect Firefox Getting Started toolbar
		if (item.id === 'toolbar_____') {
			for (const child of item.children || []) {
				if (
					child.name === 'Getting Started' &&
					child.url?.startsWith('https://www.mozilla.org')
				) {
					deselectItem(child);
				}
			}
		}

		// Deselect basic mobile webs
		if (item.id === 'mobile______') {
			for (const child of item.children || []) {
				const url = child.url || '';

				switch (child.url) {
					case 'about:firefox':
						deselectItem(child);
				}

				if (url.startsWith('https://addons.mozilla.org')) {
					deselectItem(child);
				} else if (url.startsWith('https://support.mozilla.org')) {
					deselectItem(child);
				}
			}
		}

		if (addItem) {
			newTree.push(item);
		}
	}

	return newTree;
}

function cleanTreeFromChrome(tree: BookmarkItem[]): BookmarkItem[] {
	const newTree: BookmarkItem[] = [];

	// Move items' children up
	for (const item of tree) {
		let addItem = true;

		switch ((item.name || '').trim().toLowerCase()) {
			case 'bookmarks':
			case 'bookmarks bar':
			case 'other bookmarks':
				addItem = false;
				item.children.forEach((c) => {
					c.parent = item.parent;
				});
				newTree.push(...item.children);
		}

		if (addItem) {
			newTree.push(item);
		}
	}

	return newTree;
}

function deselectItem(item: BookmarkItem, propagate = false): void {
	item.selected = BookmarkSelectState.UNSELECTED;
	if (propagate) {
		for (const child of item.children || []) {
			deselectItem(child, true);
		}
	}
}

export function getBrowser(): 'firefox' | 'chrome' | 'other' {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	// Firefox 1.0+
	const isFirefox = typeof InstallTrigger !== 'undefined';
	if (isFirefox) {
		return 'firefox';
	}

	const hasChrome = window.navigator.userAgent.includes('Chrome');
	// Chrome 1 - 79
	const isChrome = !!hasChrome;
	if (isChrome) {
		return 'chrome';
	}

	return 'other';
}
